"use client";

import { useCallback, useEffect, useRef, useState } from "react";

export type AutoSaveStatus =
  | "idle"
  | "dirty"
  | "saving"
  | "saved"
  | "error";

export interface UseAutoSaveOptions<T> {
  /** Current form value */
  value: T;
  /** Server's known value, used to detect dirtiness */
  savedValue: T;
  /** Custom equality check. Defaults to Object.is. */
  isEqual?: (a: T, b: T) => boolean;
  /** Debounce window after the user stops changing the value. Defaults to 1000ms. */
  debounceMs?: number;
  /** Fired when a save should be performed (e.g. a React Query mutate call). */
  onSave: (value: T) => void;
  /** Fired from `pagehide`. Implement with `fetch(..., { keepalive: true })`. */
  onBeaconSave?: (value: T) => void;
  /** From useMutation: whether a save is in flight. */
  isPending: boolean;
  /** From useMutation: whether the last save errored. */
  isError: boolean;
}

export interface UseAutoSaveResult {
  status: AutoSaveStatus;
  /** Save now, cancelling any pending debounce. Safe to call repeatedly. */
  flush: () => void;
}

const SAVED_INDICATOR_MS = 2000;

export function useAutoSave<T>({
  value,
  savedValue,
  isEqual = Object.is,
  debounceMs = 1000,
  onSave,
  onBeaconSave,
  isPending,
  isError,
}: UseAutoSaveOptions<T>): UseAutoSaveResult {
  const valueRef = useRef(value);
  const savedValueRef = useRef(savedValue);
  const isEqualRef = useRef(isEqual);
  const onSaveRef = useRef(onSave);
  const onBeaconSaveRef = useRef(onBeaconSave);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wasPendingRef = useRef(false);
  const didBeaconSaveRef = useRef(false);
  const attemptedRef = useRef<{ value: T } | null>(null);
  const [showSaved, setShowSaved] = useState(false);

  // Keep refs current so trigger callbacks always see latest values.
  // Runs after every commit; safe because all consumers (timers, event
  // handlers, cleanups) fire after the effect runs.
  useEffect(() => {
    valueRef.current = value;
    savedValueRef.current = savedValue;
    isEqualRef.current = isEqual;
    onSaveRef.current = onSave;
    onBeaconSaveRef.current = onBeaconSave;
  });

  const isDirty = !isEqual(value, savedValue);

  const clearDebounce = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
  }, []);

  // Every save goes through here so `attemptedRef` always holds the value of
  // the most recent attempt — the failure gate in trigger 1 needs it.
  const save = useCallback((next: T) => {
    attemptedRef.current = { value: next };
    onSaveRef.current(next);
  }, []);

  const flush = useCallback(() => {
    clearDebounce();
    if (!isEqualRef.current(valueRef.current, savedValueRef.current)) {
      save(valueRef.current);
    }
  }, [clearDebounce, save]);

  // Trigger 1: debounce-on-change. Gated on !isPending so we don't queue
  // a redundant PATCH while one is already in flight — under slow networks
  // the same edit would otherwise be sent multiple times. When the in-flight
  // save resolves, isPending flips to false; if there is still a draft
  // (isDirty true after savedValue catches up via setQueryData), this effect
  // re-runs and schedules the next save.
  useEffect(() => {
    if (!isDirty || isPending) {
      clearDebounce();
      return;
    }
    // A failed save must not re-arm itself. `savedValue` never catches up when
    // the write fails, so `isDirty` stays true and the isPending true→false
    // edge would schedule the identical request again, forever (one error
    // toast per debounce tick). Retry only once the value changes — an edit,
    // or a `flush` the user triggered deliberately.
    if (
      isError &&
      attemptedRef.current &&
      isEqualRef.current(value, attemptedRef.current.value)
    ) {
      clearDebounce();
      return;
    }
    clearDebounce();
    debounceTimerRef.current = setTimeout(() => {
      save(valueRef.current);
    }, debounceMs);
    return clearDebounce;
  }, [value, isDirty, isPending, isError, debounceMs, clearDebounce, save]);

  // Show "Saved" briefly when a save transitions pending → success.
  useEffect(() => {
    if (isPending) {
      wasPendingRef.current = true;
      setShowSaved(false);
      return;
    }
    if (wasPendingRef.current && !isError) {
      wasPendingRef.current = false;
      setShowSaved(true);
      const timer = setTimeout(() => setShowSaved(false), SAVED_INDICATOR_MS);
      return () => clearTimeout(timer);
    }
    if (wasPendingRef.current && isError) {
      wasPendingRef.current = false;
    }
  }, [isPending, isError]);

  // Trigger 2: Cmd/Ctrl+S.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "s" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        flush();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [flush]);

  // Trigger 3: pagehide keepalive beacon.
  useEffect(() => {
    const onPageHide = () => {
      if (!onBeaconSaveRef.current) return;
      if (!isEqualRef.current(valueRef.current, savedValueRef.current)) {
        onBeaconSaveRef.current(valueRef.current);
        // Mark that the beacon already covered this dirty value so the
        // unmount cleanup below doesn't fire a second redundant save.
        didBeaconSaveRef.current = true;
      }
    };
    window.addEventListener("pagehide", onPageHide);
    return () => window.removeEventListener("pagehide", onPageHide);
  }, []);

  // Trigger 4: unmount flush. Cleanup runs on SPA navigation away.
  useEffect(() => {
    return () => {
      clearDebounce();
      if (didBeaconSaveRef.current) return;
      if (!isEqualRef.current(valueRef.current, savedValueRef.current)) {
        onSaveRef.current(valueRef.current);
      }
    };
  }, [clearDebounce]);

  let status: AutoSaveStatus;
  if (isPending) status = "saving";
  else if (isError) status = "error";
  else if (isDirty) status = "dirty";
  else if (showSaved) status = "saved";
  else status = "idle";

  return { status, flush };
}
